// Third-party
import React, { Component } from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { SortingRule } from 'react-table';
import { withRouter, RouteComponentProps } from 'react-router-dom';

// Utils
import { FormattedMessage, formatMessage } from '../../../../../utils/i18n';
import { truncateString } from '../../../../../utils/objects';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '../../../../../utils/permissions';
import { canAccessUploadCSV } from '@/utils/feature-access';
import {
  clonePortfolioAsProxy,
  getPortfolioMissingHoldings,
  loadPortfolioIfHoldingsAreMissing,
  loadPortfoliosIfHoldingsAreMissing,
} from '../../../../../utils/portfolio/utils';
import { withLoaderAction } from '@/utils/promise';
import notification from '../../../../../utils/notification';
import { showError } from '../../../../../utils/errors';
import heapUtils, { HEAP_CUSTOM_EVENTS } from '../../../../../utils/3rd-party/heap';
import disclaimers from '../../../../../utils/disclaimers';
import { NAV_PATHS, PortfolioFlag } from '../../../../../utils/constants';
import { HoldingAllocationMode, PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '@/enums/Portfolio';
import localStorageUtils from '@/utils/local-storage';
import { resetLocalStoragePersistenceSource } from '@/utils/persistence';

// Types
import Dictionary from '@/types/dictionary';
import GenericContact from '@/types/generic-contact';
import User from '@/types/user';
import Portfolio from '@/types/portfolio';
import { DataSourceTabs as DataSourceTabsType } from '@/types/app-config';
import { SortingField, SortOrder } from '@/types/data';

// Enums
import { PERSISTENCE_KEYS } from '@/utils/persistence';
import { DATA_SOURCE_TAB_CATEGORY } from '@/enums/DataSource';

// Redux
import { ReduxState } from '../../../../../redux/modules/types';
import { selectIsLoadingEvery, selectIsLoadingParamsEvery, startLoaderAction, stopLoaderAction } from '../../../../../redux/modules/ui/loaders';
import { addPortfoliosToComparison, ADD_PORTFOLIOS_TO_COMPARISON } from '../../../../../redux/modules/comparison';
import {
  deleteContactPortfolios,
  DELETE_CONTACT_PORTFOLIOS,
  getContactPortfolios,
  saveContactPortfolio,
  acceptContactPortfolio,
  setPersistenceSource,
} from '../../../../../redux/modules/contacts';
import { loadPortfolioForEdit, resetPortfolio, setPortfolioContact } from '../../../../../redux/modules/edit-portfolio';
import { logNewPortfolio } from '../../../../../services/usage-log';
import { togglePortfolioEditor } from '@/redux/modules/common/actionCreators';
import { getPortfolioById } from '../../../../../redux/modules/portfolios';
import { setIsActivePortfolioPostMessage } from '@/redux/modules/post-message';

// Components
import ConnectedPrivateComponent from '../../../../components/PrivateComponent';
import DataSourceTabs from '@/views/components/data-source/DataSourceTabs';
import DropdownButton from '@/views/components/buttons/DropdownButton';
import PortfolioUploadModal from '@/views/components/modals/portfolio-upload/PortfolioUploadModal';
import { PrimaryButton } from '../../../../components/buttons';
import { SearchInput } from '../../../../components/inputs';
import { PortfoliosTable } from '../../../../components/tables';
import { GenericEmptyState } from '../../../../components/misc';
import { LoadingSpinner } from '../../../../components/common';
import { DeletePortfoliosModal, EditPortfolioModal } from '../../../../components/modals';
import { getFilteredDataSourceTabs } from '@/utils/data-source';

type MatchParams = {
  contactId: string;
};

type StateProps = {
  contact?: GenericContact;
  user?: User | null;
  portfolios?: Portfolio[];
  persistenceSource?: string;
  loading: boolean;
  comparingPortfolioIds: string[];
  isDeletingContactPortfolio: boolean;
  hasMorePortfolios: boolean;
  editPortfolioModalIsVisible: boolean;
  isPostMessageActive: boolean;
};

type DispatchProps = {
  addPortfoliosToComparison: (portfolios: Portfolio[]) => Promise<void>;
  deleteContactPortfolios: (portfolios: Portfolio[], contactId: string) => Promise<{ portfolioIds: string[] }>;
  getContactPortfolios: (query?: string, sort?: SortingField[], shouldFetchNext?: boolean) => Promise<Portfolio[]>;
  loadPortfolioForEdit: (portfolio: Portfolio) => Promise<void>;
  resetPortfolio: () => void;
  saveContactPortfolio: (portfolio: Portfolio) => Promise<Portfolio>;
  setPortfolioContact: (contact: GenericContact) => void;
  logNewPortfolio: () => Promise<void>;
  togglePortfolioEditor: (show: boolean) => void;
  getPortfolioById: (
    id: string,
    portfolioType?: string,
    contact?: string,
    enterprise?: any,
    errorWhiteList?: string[],
    source?: Portfolio,
  ) => Promise<Portfolio>;
  acceptContactPortfolio: (contactId: string, portfolioId: string) => Promise<void>;
  setIsActivePortfolioPostMessage: (isActive: boolean) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
  startLoaderAction: (name: string) => void;
  stopLoaderAction: (name: string) => void;
};

type Props = RouteComponentProps<MatchParams> &
  StateProps &
  DispatchProps & {
    nextContactPortfoliosToken?: string;
  };

type State = {
  activeDataSource: string;
  query: string;
  sortingFields: SortingField[];
  showDeletePortfolioModal: boolean;
  selectedPortfoliosForDelete: Portfolio[];
  hasPendingComparison: boolean;
  showPortfolioUploadModal: boolean;
};

export class LegacyContactPortfolios extends Component<Props, State> {
  private dataSourceTabs: DataSourceTabsType[] | null = getFilteredDataSourceTabs(
    this.props.user,
    DATA_SOURCE_TAB_CATEGORY.PORTFOLIOS,
    this.props.isPostMessageActive,
  );

  private portfoliosTable = React.createRef<PortfoliosTable<Portfolio>>();

  state = {
    activeDataSource: this.dataSourceTabs?.[0]?.value ?? '',
    query: '',
    sortingFields: [],
    showDeletePortfolioModal: false,
    selectedPortfoliosForDelete: [],
    hasPendingComparison: false,
    showPortfolioUploadModal: false,
  };

  componentDidMount() {
    const { isPostMessageActive, user, setIsActivePortfolioPostMessage } = this.props;
    if (user) {
      // Reset the Persistence Source when the component mounts
      const isPostMessageDataSourceTabExist = this.dataSourceTabs?.find(tab => tab.isPostMessage);
      const isInitialDataSourceTabPostMessage = isPostMessageActive && isPostMessageDataSourceTabExist;

      if (isInitialDataSourceTabPostMessage) {
        setIsActivePortfolioPostMessage(false);
      }
      resetLocalStoragePersistenceSource(localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE, user, () => this.searchPortfolios());
    }
  }

  componentWillUnmount() {
    this.props.getContactPortfolios('', []);
  }

  searchPortfolios = debounce(() => {
    const { query, sortingFields } = this.state;
    this.props.getContactPortfolios(query, sortingFields);
  }, 250);

  onSearch = (query: string) => {
    this.setState({ query });
    this.searchPortfolios();
  };

  onLoadMore = () => {
    this.searchPortfolios();
  };

  onDeletePortfolio = (portfolios: Portfolio[]) => {
    this.setState({
      selectedPortfoliosForDelete: portfolios,
      showDeletePortfolioModal: true,
    });
  };

  onCancelDeletePortfolio = () => {
    this.setState({ showDeletePortfolioModal: false });
  };

  onConfirmDeletePortfolios = () => {
    const { selectedPortfoliosForDelete: portfolios } = this.state;
    const { contactId } = this.props.match.params;
    if (!portfolios) {
      return;
    }

    this.props
      .deleteContactPortfolios(portfolios, contactId)
      .then(res => {
        if (portfolios.length !== res.portfolioIds.length) {
          notification.error(formatMessage('Portfolios.DeleteFailure'));
        } else {
          notification.success(formatMessage('Portfolios.DeleteSuccess'));
        }
        this.portfoliosTable.current?.unselectAll();
        this.onCancelDeletePortfolio();
      })
      .catch(error => {
        notification.error(formatMessage('Portfolios.DeleteFailure'));
      });
  };

  showEditPortfolioModal = (hasPendingComparison?: boolean) => {
    this.setState({ hasPendingComparison: !!hasPendingComparison });
    this.props.togglePortfolioEditor(true);
  };

  onEditPortfolio = (inPortfolio: Portfolio, pendingComparison?: boolean) => {
    const { user, getPortfolioById } = this.props;
    loadPortfolioIfHoldingsAreMissing(inPortfolio, getPortfolioById)
      .then(portfolio => {
        const toLoad = disclaimers.portfolioHasMissingOrInvalidHoldings(portfolio) ? clonePortfolioAsProxy(portfolio) : portfolio;

        if (user && userHasPermissionEnabled(user, PERMISSIONS.PORTFOLIO_UNITS_MODE)) {
          toLoad.allocationMode = HoldingAllocationMode.Units;
        }

        this.props
          .loadPortfolioForEdit(toLoad)
          .then(() => {
            this.showEditPortfolioModal(pendingComparison);
          })
          .catch(showError);
      })
      .catch(showError);
  };

  onAcceptPortfolio = (portfolio: Portfolio) => {
    const { acceptContactPortfolio, contact } = this.props;
    const { query } = this.state;
    if (contact?._id && portfolio._id) {
      acceptContactPortfolio(contact?._id, portfolio._id).then(() => {
        this.onSearch(query);
      });
    }
  };

  onCancelEditPortfolio = () => {
    this.props.resetPortfolio();
    this.props.togglePortfolioEditor(false);
  };

  onEditPortfolioSave = (portfolio: Portfolio): Promise<Portfolio> => {
    const { contact } = this.props;
    const { hasPendingComparison, query, sortingFields } = this.state;

    if (contact && !portfolio.contact) {
      portfolio.contact = contact;
      this.props.setPortfolioContact(contact);
      this.props.logNewPortfolio();
    }

    return this.props.saveContactPortfolio(portfolio).then((newPortfolio: Portfolio) => {
      this.props.resetPortfolio();
      if (hasPendingComparison) {
        this.onComparePortfolios([newPortfolio]);
      } else {
        this.props.togglePortfolioEditor(false);
      }
      this.props.getContactPortfolios(query, sortingFields);
      return newPortfolio;
    });
  };

  onComparePortfolios = async (portfolioList: Portfolio[]) => {
    const { getPortfolioById, startLoaderAction, stopLoaderAction } = this.props;

    await withLoaderAction(ADD_PORTFOLIOS_TO_COMPARISON.type, startLoaderAction, stopLoaderAction, async () => {
      const portfolios = await loadPortfoliosIfHoldingsAreMissing(portfolioList, getPortfolioById);

      const portfolioWithMissingHolding = portfolios.find(portfolio => disclaimers.portfolioHasMissingOrInvalidHoldings(portfolio));
      if (portfolioWithMissingHolding) {
        this.onEditPortfolio(portfolioWithMissingHolding, true);
        return;
      }

      await this.props.addPortfoliosToComparison(portfolios);
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
      this.setState({ hasPendingComparison: false });
      this.props.togglePortfolioEditor(false);
      this.props.history.push(NAV_PATHS.COMPARISON, { from: this.props.location });
    });
  };

  onCreatePortfolio = () => {
    const { contact } = this.props;
    if (!contact) {
      return;
    }

    this.props.resetPortfolio();
    this.props.setPortfolioContact(contact);
    this.showEditPortfolioModal();
    this.props.logNewPortfolio();
  };

  updateSortSetting = (sortingRules: SortingRule[]) => {
    const { query } = this.state;
    const sortingFields = sortingRules.map(rule => ({
      id: rule.id,
      order: rule.desc ? SortOrder.Desc : SortOrder.Asc,
    }));

    this.setState({ sortingFields });
    this.props.getContactPortfolios(query, sortingFields, false);
  };

  renderEmptyMessage = () => {
    const { user } = this.props;
    const canAdd = user && userHasPermission(user, PERMISSIONS.PORTFOLIOS_CREATE);

    return (
      <GenericEmptyState
        header={formatMessage('EmptyState.NoContactPortfolios.ContactTitle')}
        body={canAdd ? formatMessage('EmptyState.NoContactPortfolios.ContactText') : undefined}
        actionIcon="icon-arrow-up-right2"
        actionLabel={canAdd ? formatMessage('EmptyState.NoContactPortfolios.GetStarted') : undefined}
        onAction={this.onCreatePortfolio}
      />
    );
  };

  renderNoResultsMessage = () => (
    <GenericEmptyState
      header={formatMessage('EmptyState.NoResults.Title')}
      body={formatMessage('EmptyState.NoResults.Text')}
      actionLabel={formatMessage('EmptyState.NoResults.ClearSearch')}
      onAction={() => this.onSearch('')}
    />
  );

  renderNoResults = () => {
    const { query } = this.state;

    if (query) {
      return this.renderNoResultsMessage();
    }

    return this.renderEmptyMessage();
  };

  getDisplayedColumns = () => {
    const { user } = this.props;
    return [
      {
        key: PORTFOLIO_TABLE_COLUMNS.NAME,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LAST_MODIFIED,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LOCK,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.COMPARE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.MENU_TOGGLE,
      },
    ]
      .filter(col => !col.hidden)
      .map(col => col.key);
  };

  createDropdownItems = () => [
    {
      className: 'large-icon',
      node: formatMessage('Comparison.NewPortfolio'),
      icon: 'create-portfolio1',
      onClick: this.onCreatePortfolio,
    },
    {
      className: 'large-icon',
      node: formatMessage('Comparison.UploadCSVFile'),
      icon: 'upload-file',
      onClick: this.showPortfolioUploadModal,
      hidden: !canAccessUploadCSV(this.props.user),
    },
  ];

  showPortfolioUploadModal = () => this.setState({ showPortfolioUploadModal: true });

  closePortfolioUploadModal = () => this.setState({ showPortfolioUploadModal: false });

  onSelectDataSource = (dataSourceValue: string) => {
    const { user, setIsActivePortfolioPostMessage, setPersistenceSource } = this.props;

    if (!user || !this.dataSourceTabs) return;

    this.setState({ activeDataSource: dataSourceValue });

    const activeDataSource = this.dataSourceTabs.find(tab => tab.value === dataSourceValue);
    let persistenceSource;

    if (activeDataSource?.isPostMessage) {
      setIsActivePortfolioPostMessage(true);
    } else {
      setIsActivePortfolioPostMessage(false);
      persistenceSource = activeDataSource?.persistenceKey;
    }

    setPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE, persistenceSource);
    this.searchPortfolios();
  };

  onCanAccept = (portfolio: Portfolio): boolean => {
    const { user } = this.props;

    return (
      user != null &&
      userHasPermission(user, PERMISSIONS.CONTACTS_PORTFOLIOS_ACCEPT) &&
      localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE) == null &&
      !portfolio.flags?.includes(PortfolioFlag.AcceptedPortfolio)
    );
  };

  onCanEdit = (portfolio: Portfolio): boolean => {
    const { user } = this.props;

    return userHasPermission(user, PERMISSIONS.PORTFOLIOS_EDIT) && (!portfolio.availableActions || portfolio.availableActions.edit);
  };

  onCanClone = (portfolio: Portfolio): boolean => {
    const { user } = this.props;

    return userHasPermission(user, PERMISSIONS.PORTFOLIOS_CLONE) && (!portfolio.availableActions || portfolio.availableActions.clone);
  };

  onCanDelete = (portfolio: Portfolio): boolean => {
    const { user } = this.props;

    return userHasPermission(user, PERMISSIONS.PORTFOLIOS_DELETE) && (!portfolio.availableActions || portfolio.availableActions.delete);
  };

  render() {
    const { user, contact, portfolios, loading, hasMorePortfolios, comparingPortfolioIds, isDeletingContactPortfolio, editPortfolioModalIsVisible } =
      this.props;
    const { activeDataSource, query, showDeletePortfolioModal, selectedPortfoliosForDelete, showPortfolioUploadModal } = this.state;

    const displayedColumns = this.getDisplayedColumns();
    const displayedMetrics = [
      PORTFOLIO_METRICS_FIELDS.MER,
      PORTFOLIO_METRICS_FIELDS.YEAR_ONE,
      PORTFOLIO_METRICS_FIELDS.YEAR_THREE,
      PORTFOLIO_METRICS_FIELDS.SIZE,
      PORTFOLIO_METRICS_FIELDS.HOLDINGS,
    ];
    const isTableHidden = loading || !portfolios || portfolios.length === 0;
    const hasPersistenceKeySource =
      user != null && localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE) != null;

    // Data Source Tabs related logic
    const isContactPersistenceSourceActive =
      user && localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE) != null;
    const contactDataSourceTabs: DataSourceTabsType[] = get(user, 'appConfig.dataSourceTabs.contacts', []);
    const hasExternalContacts =
      !contactDataSourceTabs.filter(tab => tab.persistenceKey === PERSISTENCE_KEYS.EXISTING_CLIENT_CONTACT).length || isContactPersistenceSourceActive;

    return (
      <div className="contact-portfolios">
        <div className="content-header">
          <FormattedMessage
            data-private
            data-dd-privacy="mask"
            tagName="h1"
            id="Contacts.ContactTabs.Portfolios.Header"
            values={{ name: truncateString((contact && contact.fname) || '', 80) }}
          />

          {this.dataSourceTabs && hasExternalContacts && (
            <DataSourceTabs disabled={loading} tabs={this.dataSourceTabs} value={activeDataSource} onChange={this.onSelectDataSource} />
          )}

          <div className="content-actions d-flex">
            <ConnectedPrivateComponent permissions={[PERMISSIONS.PORTFOLIOS_FILTER]}>
              {() => <SearchInput onChange={this.onSearch} value={query} placeholder={formatMessage('Contacts.ContactTabs.Portfolios.SearchPlaceholder')} />}
            </ConnectedPrivateComponent>

            <ConnectedPrivateComponent forcedFalse={hasPersistenceKeySource} permissions={[PERMISSIONS.PORTFOLIOS_CREATE]}>
              {() => (
                <DropdownButton
                  toggle={{
                    component: PrimaryButton,
                    dark: true,
                    icon: 'plus-med',
                    className: 'create-portfolios',
                  }}
                  align="end"
                  items={this.createDropdownItems()}
                >
                  <FormattedMessage id="Contacts.ContactTabs.Portfolios.Create" />
                </DropdownButton>
              )}
            </ConnectedPrivateComponent>
          </div>
        </div>

        <div className="content-body">
          {loading && <LoadingSpinner className="initial-loading" message={formatMessage('Contacts.ContactTabs.Portfolios.LoadingPortfolios')} />}

          {!loading && portfolios && portfolios.length === 0 && this.renderNoResults()}

          <PortfoliosTable<Portfolio>
            ref={this.portfoliosTable}
            className={classNames({ 'visually-hidden': isTableHidden })}
            comparingPortfolioIds={comparingPortfolioIds}
            displayedColumns={displayedColumns}
            displayedMetrics={displayedMetrics}
            showLoadMore={hasMorePortfolios}
            portfolios={portfolios}
            selectedTitle="PortfoliosTable.SelectedTitle.Portfolios"
            onCompare={this.onComparePortfolios}
            onDelete={this.onDeletePortfolio}
            onEdit={this.onEditPortfolio}
            onAccept={this.onAcceptPortfolio}
            onLoadMore={this.onLoadMore}
            onSort={this.updateSortSetting}
            missingProductPredicate={portfolio => getPortfolioMissingHoldings(portfolio).length > 0}
            canEditPredicate={portfolio => this.onCanEdit(portfolio)}
            canDeletePredicate={portfolio => this.onCanDelete(portfolio)}
            canClonePredicate={portfolio => this.onCanClone(portfolio)}
            canAcceptPredicate={portfolio => this.onCanAccept(portfolio)}
          />
        </div>

        <DeletePortfoliosModal
          isDeleting={isDeletingContactPortfolio}
          portfolios={selectedPortfoliosForDelete}
          show={showDeletePortfolioModal}
          onCancel={this.onCancelDeletePortfolio}
          onConfirm={this.onConfirmDeletePortfolios}
        />

        <EditPortfolioModal show={editPortfolioModalIsVisible} onCancel={this.onCancelEditPortfolio} onSave={this.onEditPortfolioSave} />

        <PortfolioUploadModal show={showPortfolioUploadModal} onCancel={this.closePortfolioUploadModal} titleId="Modal.PortfolioUpload.UploadClientPortfolio" />
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => {
  const loadingParams = selectIsLoadingParamsEvery(state, [ADD_PORTFOLIOS_TO_COMPARISON.type]);
  const comparingPortfolioIds = loadingParams.reduce((acc, param: Dictionary<unknown>) => {
    if (param.portfolios) {
      return [...acc, ...(param.portfolios as Portfolio[]).map(portfolio => portfolio._id!)];
    }

    return acc;
  }, [] as string[]);

  return {
    hasMorePortfolios: !!state.contacts.current?.hasMore.portfolios,
    contact: state.contacts.current?.contact,
    persistenceSource: state.contacts.persistenceSource,
    user: state.auth.user,
    portfolios: state.contacts.current?.portfolios,
    loading: state.contacts.loading.current.portfolios,
    comparingPortfolioIds,
    isDeletingContactPortfolio: selectIsLoadingEvery(state, [DELETE_CONTACT_PORTFOLIOS.type]),
    editPortfolioModalIsVisible: state.comparison.portfolioEditor.isVisible,
    isPostMessageActive: state.postMessage.isPostMessageActive,
  };
};

export default withRouter(
  connect(mapStateToProps, {
    resetPortfolio,
    addPortfoliosToComparison,
    deleteContactPortfolios,
    getContactPortfolios,
    loadPortfolioForEdit,
    saveContactPortfolio,
    setPortfolioContact,
    logNewPortfolio,
    togglePortfolioEditor,
    getPortfolioById,
    acceptContactPortfolio,
    setIsActivePortfolioPostMessage,
    setPersistenceSource,
    startLoaderAction,
    stopLoaderAction,
  })(LegacyContactPortfolios),
);
